import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    InputBase,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import { StyledTextField } from '../../Common/StyledTextField';
import { getSingleFranchise, updateFranchiseBasicDetails } from '../../../Services/authService';
import LoadingAnimation from '../../Common/LoadingAnimation';
import countryCodes from '../full_country_calling_codes.json';

function AccountUpdate({ handleClose, onSuccess }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const textColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
    const cardBorderColor = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
    const errorTextColor = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

    const [accountData, setAccountData] = useState({
        name: '',
        id: '',
        taxIdNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        email: '',
        phoneNumber: ''
    });
    const [originalData, setOriginalData] = useState({});
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [countryCode, setCountryCode] = useState({ name: "Germany", dial_code: "+49" });
    const [phoneNumber, setPhoneNumber] = useState('');

    // Load Franchise Details
   useEffect(() => {
    setErrors({});
    setGeneralError('');

    const fetchData = async () => {
        setLoading(true);

        try {
            const response = await getSingleFranchise();

          

            const franchiseData = response || {};

          

            setAccountData(franchiseData);
            setOriginalData(franchiseData);

            if (franchiseData?.phoneNumber) {
                const matched = countryCodes.find(c =>
                    franchiseData.phoneNumber.startsWith(c.dial_code)
                );

                if (matched) {
                    setCountryCode(matched);
                    setPhoneNumber(
                        franchiseData.phoneNumber
                            .replace(matched.dial_code, '')
                            .trim()
                    );
                } else {
                    setPhoneNumber(franchiseData.phoneNumber);
                }
            }
        } catch (error) {
            console.error(
                t('navbar.errorFetchingFranchiseDetails'),
                error
            );

            setGeneralError(
                t('navbar.failedLoadFranchiseDetails')
            );
        } finally {
            setLoading(false);
        }
    };

    fetchData();
}, []);

    const hasUnsavedChanges = () => {
        return JSON.stringify(accountData) !== JSON.stringify(originalData);
    };

    // const handleCancel = () => {
    //     if (hasUnsavedChanges()) {
    //         setConfirmModal(true);
    //     } else {
    //         handleClose();
    //     }
    // };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Name is capped at 100 characters while typing
        if (name === "name" && value.length > 100) return;
        setAccountData({
            ...accountData,
            [name]: value,
        });
    };

   const validate = () => {
    let tempErrors = {};

    if (!accountData.name?.trim()) {
        tempErrors.name = t('navbar.companyNameRequired');
    } else if (
        accountData.name.trim().length < 2 ||
        accountData.name.trim().length > 100
    ) {
        tempErrors.name = t('membershipCards.nameMustBe2to100');
    }

    if (!accountData.taxIdNumber) {
        tempErrors.taxIdNumber = t('navbar.taxNumberRequired');
    }

    if (!phoneNumber) {
        tempErrors.phoneNumber = t('navbar.validPhoneNumberRequired');
    } else if (!/^\d{5,15}$/.test(phoneNumber)) {
        tempErrors.phoneNumber = t('membershipCards.phoneNumberMustBe5to15');
    }

    if (!accountData.email) {
        tempErrors.email = t('navbar.emailRequired');
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(accountData.email)) {
        tempErrors.email = t('navbar.invalidEmailFormat');
    }

    if (!accountData.addressLine1) {
        tempErrors.addressLine1 = t('navbar.addressRequired');
    }

    if (!accountData.city) {
        tempErrors.city = t('navbar.cityRequired');
    }

    if (!accountData.state) {
        tempErrors.state = t('navbar.stateRequired');
    }

    if (!accountData.country) {
        tempErrors.country = t('navbar.countryRequired');
    }

    if (!accountData.zipCode) {
        tempErrors.zipCode = t('navbar.zipCodeRequired');
    } else if (!/^\d{5,6}$/.test(accountData.zipCode)) {
        tempErrors.zipCode = t('navbar.zipCodeMustBe5to6Digits');
    }

    setErrors(tempErrors);

    setGeneralError(
        Object.keys(tempErrors).length > 0
            ? (tempErrors.phoneNumber ||
               tempErrors.name ||
               t('navbar.fillAllRequiredFields'))
            : ''
    );

    return Object.keys(tempErrors).length === 0;
};

    const handleSubmit = async () => {
        setGeneralError('')
        if (validate()) {
            try {
                // if (!hasUnsavedChanges) {
                //     setGeneralError('Change anything and click save button');
                // }
                setLoading(true);
                // Create accountData object
                // const accountDataToSend = new FormData();
                // accountDataToSend.append("franchise", JSON.stringify(accountData));
                // if (imageFile) accountDataToSend.append("logo", imageFile);
                // console.log(t('navbar.debugAccountData'), accountData);


                await updateFranchiseBasicDetails(accountData?.id, accountData);
                setOriginalData(accountData);
                onSuccess(); // parent handles notification + close
            } catch (error) {
                const errorMessage = error.response?.data?.detail || t('navbar.unexpectedError');
                console.error(t('navbar.errorUpdatingFranchise'), error);
                setGeneralError(errorMessage);
            } finally {
                setLoading(false);
            }
        } else {
            // validate() already surfaced the field/general error message
        }
    };

    return (
        <>
            {loading && <LoadingAnimation />}
            {/* <Typography variant="subtitle1">{t('navbar.Details')}</Typography> */}
            {/* name */}
            <Box>
                <StyledTextField
                    label={`${t('franchise.CompanyName')}`}
                    name="name"
                    value={accountData.name}
                    onChange={handleChange}
                    error={errors.name}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('navbar.TaxIDNumber')}`}
                    name="taxIdNumber"
                    value={accountData.taxIdNumber}
                    onChange={handleChange}
                    error={errors.taxIdNumber}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            {/* phonennumber */}
            <Box>
                {/* <StyledTextField
                    label={`${t('loginPage.phoneNumber')}`}
                    name="phoneNumber"
                    value={accountData.phoneNumber}
                    onChange={handleChange}
                    error={errors.phoneNumber}
                    inputBoxColor={inputBoxColor}
                    required
                /> */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: inputBoxColor,
                        height: '50px',
                    }}
                >
                    <Autocomplete
                        options={countryCodes}
                        getOptionLabel={(option) => `${option.name} (${option.dial_code})`}
                        value={countryCode}
                        onChange={(e, newValue) => {
                            if (newValue) {
                                setCountryCode(newValue);
                                setAccountData(prev => ({
                                    ...prev,
                                    phoneNumber: `${newValue.dial_code} ${phoneNumber}`.trim(),
                                }));
                            }
                        }}
                        isOptionEqualToValue={(option, value) => option.dial_code === value.dial_code}
                        sx={{
                            minWidth: 185,
                            '& .MuiInputBase-root': {
                                height: '48px',
                                borderRight: `1px solid ${cardBorderColor}`,
                                borderRadius: 0,
                                backgroundColor: 'transparent',
                                px: 2,
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                InputProps={{ ...params.InputProps, disableUnderline: true, style: { color: errors.phoneNumber ? 'red' : 'inherit' } }}
                            />
                        )}
                    />
                    <InputBase
                        placeholder={`${t('membershipCards.mobileNumber')} *`}
                        value={phoneNumber}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
                            setPhoneNumber(digitsOnly);
                            if (errors.phoneNumber) {
                                setErrors(prev => ({ ...prev, phoneNumber: '' }));
                                setGeneralError('');
                            }
                            setAccountData(prev => ({
                                ...prev,
                                phoneNumber: `${countryCode.dial_code} ${digitsOnly}`.trim(),
                            }));
                        }}
                        inputProps={{ style: { color: errors.phoneNumber ? 'red' : 'inherit' } }}
                        sx={{ px: 2, flex: 1, height: '100%', color: textColor }}
                    />
                </Box>
            </Box>
            {/* Email */}
            <Box>
                <StyledTextField
                    label={`${t('loginPage.email')}`}
                    name="email"
                    value={accountData.email}
                    onChange={handleChange}
                    error={errors.email}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('membershipCards.addressLine1')}`}
                    name="addressLine1"
                    value={accountData.addressLine1}
                    onChange={handleChange}
                    error={errors.addressLine1}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('membershipCards.addressLine2')}`}
                    name="addressLine2"
                    value={accountData.addressLine2}
                    onChange={handleChange}
                    error={errors.addressLine2}
                    inputBoxColor={inputBoxColor}
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('membershipCards.city')}`}
                    name="city"
                    value={accountData.city}
                    onChange={handleChange}
                    error={errors.city}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('membershipCards.state')}`}
                    name="state"
                    value={accountData.state}
                    onChange={handleChange}
                    error={errors.state}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('franchise.Country')}`}
                    name="country"
                    value={accountData.country}
                    onChange={handleChange}
                    error={errors.country}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>
            <Box>
                <StyledTextField
                    label={`${t('membershipCards.zipCode')}`}
                    name="zipCode"
                    value={accountData.zipCode}
                    onChange={handleChange}
                    error={errors.zipCode}
                    inputBoxColor={inputBoxColor}
                    required
                />
            </Box>

            {generalError && (
                <Typography color="error" variant="body2" sx={{ textAlign: "center", marginTop: 1 }}>
                    {generalError}
                </Typography>
            )}
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <button type="button" style={{ color: btnColor, borderColor: btnColor }} className='cancel-btn' onClick={handleClose}>{t('membershipCards.cancel')}</button>
                <button type="button" style={{ background: btnColor }} className='save-btn' onClick={handleSubmit}>{t('membershipCards.save')}</button>
            </Box>

            {/* Use Notification Component — handled by parent (Profile.js) */}
        </>
    )
}

export default AccountUpdate